/* eslint-disable import/order */
import React, {useState, useCallback, useEffect, useMemo} from 'react';

import {useStore} from 'effector-react';
import {toast} from 'react-toastify';
import {useDropzone} from 'react-dropzone';

import {useTranslation} from 'react-i18next';

import {
    ParsedDataExpected,
    useParseInvoiceLinesXlsx,
    XlsxFileInvalidRow,
    XlsxFileParsedData,
} from 'components/pages/DocumentsCreate/InvoiceNew/hooks/useParseInvoiceLinesXlsx';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';

import {$invoiceLinesHolder, $invoiceLinesHolderCodesTotal} from 'models/documents/invoices/create';
import {ButtonWrapper, ModalContentErrorsOverflow} from './styled';

import {ModalContentBody} from './ModalContentBody';
import {InvalidRows} from './ModalInvalidRows';
import {LoaderDoted} from 'components/ui-kit/LoaderDoted';

interface ModalParseLinesFromXlsxProps {
    show: boolean;
    onClose: () => void;
    onFileChange?: (file: File | null) => void;
    onDataParsed?: (x: XlsxFileParsedData) => void;
    xlsxErrors: XlsxFileInvalidRow;
    setXlsxErrors: (errors: XlsxFileInvalidRow) => void;
    combineByGtin: boolean;
    setCombineByGtin: (value: boolean) => void;
    dataParsingPending: boolean;
}
export const ModalParseLinesFromXlsx: React.FC<ModalParseLinesFromXlsxProps> = ({
    show,
    onClose,
    onDataParsed,
    xlsxErrors,
    setXlsxErrors,
    combineByGtin,
    setCombineByGtin,
    dataParsingPending,
}) => {
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const {t} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines.xlsxModal';
    const [file, setFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<XlsxFileParsedData | null>(null);

    useEffect(() => {
        if (!show) {
            onCloseModal();
        }
    }, [show]);

    function onDataParsed123(data: XlsxFileParsedData) {
        setParsedData(data);
        onDataParsed?.(data);
    }

    const validateXlsxFile = useCallback(
        (data: any[]) => {
            const requiredFields: (keyof ParsedDataExpected)[] = [
                'markingCode',
                'price',
                'category',
                'vat',
            ];

            const chargesRequiredFields: (keyof ParsedDataExpected)[] = [
                'reason',
                'amount',
                'charges',
            ];

            function validateEmptyFile() {
                return {
                    0: {
                        error: [t(`${GEN_INFO}.errors.theFileIsEmpty`)],
                    },
                };
            }

            if (data.length === 0) return validateEmptyFile();

            const categoriesWithZeroVat = ['O', 'K', 'Z'];
            const setErrorText = (acc: XlsxFileInvalidRow, index: number, error: string) => {
                acc[index]
                    ? acc[index].error.push(error)
                    : (acc[index] = {
                          error: [error],
                      });
            };

            function validateData(data: ParsedDataExpected[]) {
                const markingCodes: Record<string, string> = {};
                return data!.reduce(
                    (acc, item, index) => {
                        if (item.markingCode.startsWith('(00)')) {
                            setErrorText(
                                acc,
                                index,
                                t(`${GEN_INFO}.errors.markingCodeIsTransport`, {
                                    markingCode: item.markingCode,
                                }),
                            );
                        }
                        if (markingCodes[item.markingCode]) {
                            setErrorText(
                                acc,
                                index,
                                t(`${GEN_INFO}.errors.markingCodeExists`, {
                                    markingCode: item.markingCode,
                                }),
                            );
                        }

                        if ($invoiceLinesHolderCodesTotal.getState().includes(item.markingCode)) {
                            setErrorText(
                                acc,
                                index,
                                t(`${GEN_INFO}.errors.markingCodeExists`, {
                                    markingCode: item.markingCode,
                                }),
                            );
                        }

                        markingCodes[item.markingCode] = item.markingCode;

                        const isChargesFieldsRequired = chargesRequiredFields.some(
                            (field) => item[field]?.toString() !== '',
                        );

                        const missingFields = [
                            ...requiredFields,
                            ...(isChargesFieldsRequired ? chargesRequiredFields : []),
                        ].filter((field) => item[field]?.toString() === '');
                        if (missingFields.length > 0) {
                            setErrorText(
                                acc,
                                index,
                                t(`${GEN_INFO}.errors.missingFields`, {
                                    fields: missingFields.join(', '),
                                }),
                            );
                        }
                        if (item.category && item.vat) {
                            categoriesWithZeroVat.includes(item.category) &&
                                item.vat.toString() !== '0' &&
                                setErrorText(acc, index, t(`${GEN_INFO}.errors.vat0`));
                        }
                        return acc;
                    },
                    {} as {
                        [key: number]: {
                            error: string[];
                        };
                    },
                );
            }

            return validateData(data);
        },
        [invoiceLinesHolder, t],
    );

    function onError(error: string) {
        onCloseModal();
        toast.error(
            <div>
                <strong>Errors</strong>
                <div>{error}</div>
            </div>,
        );
    }

    const {parseXlsxFile, isProcessing} = useParseInvoiceLinesXlsx({
        validateXlsxFile,
        onDataParsed: onDataParsed123,
        onError,
    });

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                const selectedFile = acceptedFiles[0];
                setFile(selectedFile);
                parseXlsxFile(selectedFile);
            }
        },
        [parseXlsxFile],
    );
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});

    const onCloseModal = () => {
        setFile(null);
        setParsedData(null);
        setXlsxErrors({});
        onClose();
        setCombineByGtin(false);
    };

    function resetFile() {
        setFile(null);
        setParsedData(null);
        setXlsxErrors({});
    }

    const MAX_ROWS_TO_SHOW = 3;

    // useEffect(() => {
    //     if (parsedData && parsedData.data && !parsedData.hasErrors) {
    //         console.log(parsedData.data);
    //         onCloseModal();
    //     }
    // }, [parsedData]);

    const invalidRows = useMemo(() => {
        if (Object.keys(xlsxErrors).length > 0) {
            const rows = Object.entries(xlsxErrors);

            const limitedRows =
                rows.length > MAX_ROWS_TO_SHOW ? rows.slice(0, MAX_ROWS_TO_SHOW) : rows;

            return {
                rows: limitedRows,
                fullLength: rows.length,
            };
        }
        if (!parsedData) {
            return {
                rows: [],
                fullLength: 0,
            };
        }
        const rows = Object.entries(parsedData.invalidRows);

        const limitedRows = rows.length > MAX_ROWS_TO_SHOW ? rows.slice(0, MAX_ROWS_TO_SHOW) : rows;
        return {
            rows: limitedRows,
            fullLength: Object.entries(parsedData.invalidRows).length,
        };
    }, [parsedData, xlsxErrors]);

    return (
        <Modal
            show={show}
            onClose={onCloseModal}
            title={parsedData?.hasErrors ? t(`${GEN_INFO}.errorsTitle`) : t(`${GEN_INFO}.title`)}
            modalWidth={0}
            closable={!dataParsingPending}
        >
            {(parsedData && parsedData.hasErrors) || invalidRows.fullLength > 0 ? (
                <>
                    <InvalidRows invalidRows={invalidRows} t={t} GEN_INFO={GEN_INFO} />
                    {invalidRows.fullLength > MAX_ROWS_TO_SHOW ? (
                        <ModalContentErrorsOverflow>
                            ... {t(`${GEN_INFO}.and`)} {invalidRows.fullLength - MAX_ROWS_TO_SHOW}{' '}
                            {t(`${GEN_INFO}.moreErrors`)}
                        </ModalContentErrorsOverflow>
                    ) : (
                        <></>
                    )}
                    <ButtonWrapper>
                        <div />
                        <Button onClick={resetFile} variant={ButtonVariant.Normal}>
                            {t(`${GEN_INFO}.uploadAgainButton`)}
                        </Button>
                    </ButtonWrapper>
                </>
            ) : dataParsingPending ? (
                <LoaderDoted />
            ) : (
                <ModalContentBody
                    isDragActive={isDragActive}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    isProcessing={isProcessing}
                    file={file}
                    t={t}
                    GEN_INFO={GEN_INFO}
                    combineByGtin={combineByGtin}
                    setCombineByGtin={setCombineByGtin}
                />
            )}
        </Modal>
    );
};
